import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { MapPin, Phone, Clock, Mail, Send, Instagram, Twitter, Facebook } from 'lucide-react'
import { siteSettingsService } from '../services/database.js'
import { messagesAPI } from '../services/messagesAPI.js'

function Contact() {
  const { language, t } = useLanguage()
  const [siteSettings, setSiteSettings] = useState({
      address: language === 'ar' ? 'شارع الملك فهد، الرياض، المملكة العربية السعودية' : 'King Fahd Road, Riyadh, Saudi Arabia',
      phone: '+966 50 123 4567',
      email: 'info@oxygengym.com',
      workingHours: language === 'ar' ? 'على مدار 24 ساعة / 7 أيام في الأسبوع' : '24 hours / 7 days a week',
      socialMedia: {
        instagram: '',
        twitter: '',
        facebook: ''
      }
  })

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = await siteSettingsService.getAll()
        if (settingsData && settingsData.length > 0) {
          const settings = settingsData[0]
          setSiteSettings({
            address: settings.address || siteSettings.address,
            phone: settings.phone || siteSettings.phone,
            email: settings.email || siteSettings.email,
            workingHours: settings.working_hours || settings.workingHours || siteSettings.workingHours,
            socialMedia: settings.social_media || siteSettings.socialMedia
          })
        }
      } catch (error) {
        console.error('Error loading settings:', error)
      }
    }
    loadSettings()
  }, [])

  const ensureHttps = (url) => {
    if (!url) return null
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return `https://${url}`
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      const newMessage = await messagesAPI.create({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: formData.message
      })
      
      setIsSubmitting(false)
      setSubmitMessage(language === 'ar' ? 'تم إرسال رسالتك بنجاح!' : 'Your message has been sent successfully!')
      setFormData({ name: '', phone: '', email: '', message: '' })
      
      setTimeout(() => setSubmitMessage(''), 3000)
    } catch (error) {
      console.error('Error submitting message:', error)
      setIsSubmitting(false)
      setSubmitMessage(language === 'ar' ? 'حدث خطأ أثناء إرسال الرسالة. يرجى المحاولة مرة أخرى.' : 'Error sending message. Please try again.')
      setTimeout(() => setSubmitMessage(''), 3000)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  return (
    <section id="contact" className="py-24 bg-dark relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(255,215,0,0.05),transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-yellow-400/10 text-yellow-400 text-sm font-medium mb-4">
            {t('getInTouch')}
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {language === 'ar' ? 'انضم إلى ' : 'Join ' }
            <span className="text-gradient">{language === 'ar' ? 'عائلتنا' : 'Our Family'}</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t('contactSubtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: language === 'ar' ? -30 : 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="glass-effect rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">
                {language === 'ar' ? 'معلومات التواصل' : 'Contact Information'}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{t('address')}</h4>
                    <p className="text-gray-400">{siteSettings.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{language === 'ar' ? 'الهاتف' : 'Phone'}</h4>
                    <p className="text-gray-400" dir="ltr">{siteSettings.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{t('email')}</h4>
                    <p className="text-gray-400">{siteSettings.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">{t('workingHours')}</h4>
                    <p className="text-gray-400">{siteSettings.workingHours}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Instagram</h4>
                    {siteSettings.socialMedia?.instagram ? (
                      <a 
                        href={ensureHttps(siteSettings.socialMedia.instagram)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-yellow-400 transition-colors"
                      >
                        {siteSettings.socialMedia.instagram}
                      </a>
                    ) : (
                      <p className="text-gray-400">-</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Twitter className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Twitter / X</h4>
                    {siteSettings.socialMedia?.twitter ? (
                      <a 
                        href={ensureHttps(siteSettings.socialMedia.twitter)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-yellow-400 transition-colors"
                      >
                        {siteSettings.socialMedia.twitter}
                      </a>
                    ) : (
                      <p className="text-gray-400">-</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Facebook className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold mb-1">Facebook</h4>
                    {siteSettings.socialMedia?.facebook ? (
                      <a 
                        href={ensureHttps(siteSettings.socialMedia.facebook)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-yellow-400 transition-colors"
                      >
                        {siteSettings.socialMedia.facebook}
                      </a>
                    ) : (
                      <p className="text-gray-400">-</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: language === 'ar' ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="glass-effect rounded-2xl p-8 space-y-6">
              <h3 className="text-2xl font-bold text-white mb-6">
                {language === 'ar' ? 'أرسل لنا رسالة' : 'Send Us a Message'}
              </h3>
              
              {submitMessage && (
                <div className="p-4 bg-green-400/20 text-green-400 rounded-xl text-center">
                  {submitMessage}
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">{t('name')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    placeholder={language === 'ar' ? 'اسمك الكامل' : 'Your full name'}
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">{t('phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                    placeholder={language === 'ar' ? 'رقم الهاتف' : 'Phone number'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 mb-2 text-sm">{t('email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors"
                  placeholder={language === 'ar' ? 'بريدك الإلكتروني' : 'Your email'}
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-2 text-sm">{t('message')}</label>
                <textarea
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-yellow-400 focus:outline-none transition-colors resize-none"
                  placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:shadow-lg hover:shadow-yellow-400/25 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
                {isSubmitting ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (language === 'ar' ? 'إرسال الرسالة' : 'Send Message')}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Contact
